import {useMemo} from 'react'
import {Empty, Space, Table, Typography} from 'antd'
import {CarOutlined, DeleteOutlined, EditOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {useIntl} from 'react-intl'
import {formatEmptyValue} from '../../../utils/common'

const PlantTable = ({
  dataSource,
  pagination = false,
  getPlantListData = null,
  onEdit = null,
  onDelete = null,
  onManageVehicles = null,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'plants.name' }),
        dataIndex: 'name',
        width: '20%',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'plants.vinIdentifier' }),
        dataIndex: 'vin_identifier',
        width: '20%',
        sorter: (a, b) =>
          (a.vin_identifier || '').localeCompare(b.vin_identifier || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'plants.timeZone' }),
        dataIndex: 'time_zone',
        width: '20%',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'plants.location' }),
        dataIndex: 'location',
        width: '20%',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'plants.make' }),
        dataIndex: 'make',
        width: '20%',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'plants.action' }),
        align: 'right',
        width: 135,
        render: (_, record) => (
          <TableActions
            actions={[
              {
                key: 'edit',
                label: intl.formatMessage({ id: 'forms.edit' }),
                icon: <EditOutlined />,
                onClick: () => onEdit(record),
              },
              {
                key: 'vehicles',
                label: intl.formatMessage({ id: 'plants.addEditVehicles' }),
                icon: <CarOutlined />,
                onClick: () => onManageVehicles(record),
              },
              {
                key: 'delete',
                label: intl.formatMessage({ id: 'forms.delete' }),
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onDelete(record),
              },
            ]}
          />
        ),
      },
    ],
    [dataSource, pagination]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `plant-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        scroll={{
          y: '70vh',
        }}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getPlantListData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default PlantTable
